/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, September 2020
 */

import React, { useRef, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Choice, Question } from '@staffino/model';
import { Box, convertCssUnit } from '@staffino/primitive-controls';
import { ScrollAnchor, useScrollEffect } from '../scroll';
import Header from '../header';
import Tooltip from '../tooltip';
import ControlledSelect from '../controls/controlled-select';
import { TextInputControl } from '../controls';
import isEnterPressed from '../input-submit';

type Props = {
  blockId: string;
  question: Question;
  choices: Choice[];
  otherChoice?: Choice;
  titleSize: Question['extraParams']['titleSize'];
  placeholder?: string;
  commentLimit?: number;
  required?: boolean;
} & React.ComponentProps<typeof Box>;

function ChoiceQuestionDropdownFragment({
  blockId,
  question,
  choices = [],
  otherChoice,
  titleSize,
  placeholder,
  commentLimit,
  required,
  ...rest
}: Props) {
  const { t } = useTranslation();
  const context = useFormContext();
  const choiceValidation = { required };
  const selectedOptionId = context?.watch(`${blockId}.choiceIds[0]`);
  const choiceError = context?.errors[blockId]?.choiceIds?.[0];

  const answerErrorMessage = useMemo(
    () => choiceError?.type === 'required' && t('Please answer this question.'),
    [choiceError, t],
  );

  const commentRef = useRef(null);
  const commentValue = context?.watch(`${blockId}.comment`);
  const commentError = context?.errors[blockId]?.comment;

  const ControlledSelectComponent = context ? Controller : ControlledSelect;
  const TextInputControlComponent = context ? Controller : TextInputControl;

  const otherChoiceWithDefaultPlaceholder = otherChoice && {
    ...otherChoice,
    title: otherChoice.title || t('Other'),
  };
  const options = useMemo(() => {
    const allChoices = otherChoice
      ? [...choices, otherChoiceWithDefaultPlaceholder]
      : choices;
    return allChoices.map((item) => ({ value: item.id, label: item.title }));
  }, [choices, otherChoice]);

  const otherOptionVisible =
    selectedOptionId && otherChoice?.id === selectedOptionId;

  const [enterTriggered, setEnterTriggered] = useState(false);
  const { scrollToError } = useScrollEffect(
    blockId,
    selectedOptionId,
    1,
    selectedOptionId !== otherChoice?.id ? 'scroll-down' : 'none',
  );
  const { scrollToError: scrollToErrorComment } = useScrollEffect(
    `${blockId}.comment`,
    enterTriggered && !commentError ? 'validValue' : null,
  );

  return (
    <>
      <ScrollAnchor id={blockId} />
      <Header
        question={question}
        size={titleSize}
        required={required}
        answerErrorMessage={answerErrorMessage}
      />
      <Box
        maxWidth={convertCssUnit('30rem')}
        width={1}
        mt={convertCssUnit(titleSize === 'small' ? '0.75rem' : '2rem')}
        mx="auto"
        {...rest}
      >
        <ControlledSelectComponent
          as={ControlledSelect}
          control={context?.control}
          name={`${blockId}.choiceIds[0]`}
          rules={choiceValidation}
          options={options}
          placeholder={placeholder || t('Type or select a choice')}
          onFocus={() => scrollToError(blockId)}
          onChange={([value]) => {
            if (value === otherChoice?.id && !commentValue) {
              setTimeout(() => commentRef?.current?.focus(), 100);
            }
            return value;
          }}
        />
        {otherOptionVisible && (
          <>
            <ScrollAnchor id={`${blockId}.comment`} focusOn={commentRef} />
            <Tooltip
              tooltip={t('This field is required.')}
              show={question.commentRequired && !question.commentTitle}
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: ({ placement }) =>
                      placement === 'top' ? [0, -3] : [0, 6],
                  },
                },
              ]}
            >
              <TextInputControlComponent
                blockId={`${blockId}.comment`}
                name={`${blockId}.comment`}
                as={
                  <TextInputControl
                    ref={(element: any) => {
                      context?.register?.(element, {
                        required: question.commentRequired,
                      });
                      commentRef.current = element;
                    }}
                  />
                }
                control={context?.control}
                rules={{ required: question.commentRequired }}
                invalid={!!commentError}
                requiredValidationMessage={
                  !!commentError &&
                  !question.commentTitle &&
                  t('Please fill out this field')
                }
                placeholder={`${question.commentLabel || t('Your answer')}${
                  question.commentRequired ? ' *' : ''
                }`}
                limit={commentLimit}
                activeColor="primary"
                width={1}
                maxWidth={convertCssUnit('30rem')}
                marginTop={convertCssUnit('0.675rem')}
                marginLeft="auto"
                marginRight="auto"
                marginBottom={0}
                onFocus={() => scrollToErrorComment(`${name}.comment`)}
                onKeyDown={(event) => setEnterTriggered(isEnterPressed(event))}
              />
            </Tooltip>
          </>
        )}
      </Box>
    </>
  );
}

export default ChoiceQuestionDropdownFragment;
